import { AnimatePresence, motion } from "motion/react";
import { Suspense, lazy, memo, useCallback, useEffect, useRef } from "react";

import styled from "styled-components/macro";

import RunningUno from "UI/RunningUno";
import { colors } from "UI/colors";

import useEscapeKey from "Utilities/Hooks/useEscapeKey";
import Portal from "Utilities/PortalMaker";
import { querySelectorAsync } from "Utilities/clickElemOnExist";

import {
  RE_ADVANCED_PRICING,
  RE_CUSTOM_FIELD_EDITOR,
} from "./overlayContentKeys";
import useOverlayModalStore from "./overlayModalStore";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ARIA_LABELS = {
  [RE_CUSTOM_FIELD_EDITOR]: "Custom field editor",
  [RE_ADVANCED_PRICING]: "Advanced pricing",
};

const childAnimations = {
  variants: {
    initial: { scale: 0.92, opacity: 0, y: 40 },
    open: { scale: 1, opacity: 1, y: 0 },
    closed: {
      scale: 0.92,
      opacity: 0,
      transition: { type: "tween", duration: 0.25, ease: "easeIn" },
    },
  },
  initial: "initial",
  animate: "open",
  exit: "closed",
  transition: { type: "spring", stiffness: 150, damping: 15, mass: 1 },
};

const OverlayContents = memo(({ contentKey, contentProps }) => {
  let Component;

  switch (contentKey) {
    case RE_CUSTOM_FIELD_EDITOR:
      Component = lazy(() =>
        import("RESetup/components/StudentInfo/CustomFieldEditor")
      );
      break;
    case RE_ADVANCED_PRICING:
      Component = lazy(() =>
        import("RESetup/components/Pricing/AdvancedPricingModal")
      );
      break;
    default:
      Component = () => null;
  }

  return (
    <Suspense fallback={<RunningUno />}>
      <Component {...contentProps} />
    </Suspense>
  );
});

const OverlayModal = memo(() => {
  const isOpen = useOverlayModalStore((s) => s.isOpen);
  const contentKey = useOverlayModalStore((s) => s.contentKey);
  const contentProps = useOverlayModalStore((s) => s.contentProps);
  const closeOverlayModal = useOverlayModalStore((s) => s.closeOverlayModal);
  const triggerElement = useOverlayModalStore((s) => s.triggerElement);
  const openedViaKeyboard = useOverlayModalStore((s) => s.openedViaKeyboard);
  const panelRef = useRef(null);

  const handleClose = useCallback(() => {
    if (contentProps?.onClose) contentProps.onClose();
    closeOverlayModal();
  }, [contentProps, closeOverlayModal]);

  useEscapeKey(handleClose, isOpen);

  // Return focus to the trigger element on close
  const triggerRef = useRef(null);
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = triggerElement;
    } else if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen, triggerElement]);

  // Focus the first interactive element when opened via keyboard.
  useEffect(() => {
    if (!isOpen || !openedViaKeyboard) return;
    const panel = panelRef.current;
    if (!panel) return;
    const controller = new AbortController();
    querySelectorAsync(FOCUSABLE_SELECTOR, {
      container: panel,
      signal: controller.signal,
    })
      .then((el) => el.focus())
      .catch(() => {});
    return () => controller.abort();
  }, [isOpen, openedViaKeyboard]);

  // Focus trap: cycle Tab within the modal
  const handleKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = [...panel.querySelectorAll(FOCUSABLE_SELECTOR)];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const portalTarget = document.getElementById("FnrOverlayHolder");

  return (
    <AnimatePresence>
      {isOpen && (
        <Portal to={portalTarget}>
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleBackdropClick}
            data-cy="overlay-modal-backdrop"
          >
            <Panel
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label={ARIA_LABELS[contentKey]}
              onKeyDown={handleKeyDown}
              {...childAnimations}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={handleClose} aria-label="Close">
                <i className="fas fa-circle-xmark" />
              </CloseBtn>
              <OverlayContents
                contentKey={contentKey}
                contentProps={contentProps}
              />
            </Panel>
          </Backdrop>
        </Portal>
      )}
    </AnimatePresence>
  );
});

export default OverlayModal;

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 7000;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled(motion.div)`
  position: relative;
  width: 35rem;
  max-height: 80vh;
  background: ${colors.white};
  border-radius: 20px;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.35),
    0 0.5rem 1.25rem rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  position: absolute;
  top: 0.875rem;
  right: 0.75rem;
  padding: 0;
  cursor: pointer;
  color: #c4c4c4;
  font-size: 2rem;
  line-height: 1;
  z-index: 10;
  transition: color 0.2s;

  &:hover {
    color: #888;
  }
`;
